/**
 * What a restore couldn't do, said plainly: items not restored, links that didn't come back, sharing taken off, folders
 * back under another name (one of theirs was there: "Acme (2)").
 */
import type { RestoreOut } from "../../api/types.gen.ts";
import { t } from "../../i18n/index.ts";
import { PlanAlertIcon } from "../../ui/icons.tsx";
import { Banner } from "../../ui/misc.tsx";

/** Whether a restore has anything to say beyond "restored". */
const hasNews = (r: RestoreOut) =>
  r.not_restored.length + r.dropped_links.length + r.unlinked.length + r.unshared.length > 0 ||
  r.restored.some((x) => x.renamed);

/** Its lines, with titles where they're known (`titles`: what the list showed, for the ones asked for). */
function lines(r: RestoreOut, titles: ReadonlyMap<string, string>): string[] {
  const name = (id: string, title?: string | null) => title || titles.get(id) || t("common.untitled");
  const restored = new Map(r.restored.map((x) => [x.id, x.title]));
  return [
    ...r.not_restored.map((x) =>
      t(x.reason === "not_deleted" ? "trash.report.notDeleted" : "trash.report.notFound", {
        title: name(x.id),
      }),
    ),
    ...r.dropped_links.map((l) =>
      t("trash.report.dropped", {
        item: name(l.item, l.item_title),
        target: name(l.target, l.target_title),
        key: l.key,
      }),
    ),
    ...r.unlinked.map((l) =>
      t("trash.report.unlinked", {
        item: name(l.item, l.item_title),
        target: name(l.target, l.target_title),
        key: l.key,
      }),
    ),
    ...r.unshared.map((id) => t("trash.report.unshared", { title: name(id, restored.get(id)) })),
    ...r.restored.flatMap((x) =>
      x.renamed ? [t("trash.report.renamed", { title: name(x.id, titles.get(x.id)), name: x.renamed })] : [],
    ),
  ];
}

/** Nothing when it all came back as it was; else a notice listing what didn't, until dismissed. */
export function RestoreReport(p: { report: RestoreOut; titles: ReadonlyMap<string, string>; onClose: () => void }) {
  if (!hasNews(p.report)) {
    return null;
  }
  return (
    <Banner icon={<PlanAlertIcon size={18} />} action={{ label: t("common.dismiss"), onClick: p.onClose }}>
      <p class="font-semibold">{t("trash.report.title")}</p>
      <ul class="list-disc pl-5">
        {lines(p.report, p.titles).map((l) => (
          <li key={l}>{l}</li>
        ))}
      </ul>
    </Banner>
  );
}
